/**
 * SearchBar — location/listing autocomplete, built on the shared `Combobox`
 * omnibox primitive (`@letsgethome/ui/combobox`).
 *
 * This component owns only the DATA + where things lead: it feeds the
 * controlled query to `useSearchSuggestions`, maps the returned sections to
 * `<ComboboxOption>`s, and names the URL each one is. The merged geometry,
 * open/close (incl. close-on-scroll), keyboard navigation, and ARIA all live in
 * the primitive — by construction, not re-implemented here.
 *
 * EVERY DESTINATION IS EXPRESSED IN THE HTML (LET-83). The free-text search is
 * a native `<form method="get" action="/{locale}/listings/{mode}" role="search">`
 * with the input named by the listings filter codec, so the server HTML says
 * what a search IS; `submit` is the SPA's enhancement over it. Each suggestion
 * is a real locale-aware `<Link href>` that the primitive merges the option
 * a11y onto (`asChild`), so a selection navigates through the anchor — mouse
 * and keyboard alike — and `afterSelect` only tidies up. This file therefore
 * contains no `router.push` reached from any click: the
 * `no-navigation-in-activation-handler` rule would report one.
 *
 * The panel body is a TOTAL function of the hook's `SuggestionViewState`: an
 * exhaustive `switch` ending in `assertNever`, so every state has to be given
 * a rendering and a sixth state would not compile. It used to be
 * `sections.map(…)` under a primitive that only opened when there were
 * options, which is why a query that matched nothing — 「豪景花園」 against
 * staging's five-estate seed — was pixel-identical to a broken page (LET-78).
 */
import { Link, localizeHref, useRouter, useUrlLocale } from '@/shared/i18n/routing';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { suggestionViewStateHasNotice } from '@letsgethome/lib/search';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { LISTINGS_FILTER_PARAM_NAMES, routes, type ListingsMode } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';
import {
  Combobox,
  ComboboxGroup,
  ComboboxInput,
  ComboboxNotice,
  ComboboxOption,
  ComboboxPanel,
  type ComboboxVariant,
} from '@letsgethome/ui/combobox';
import { Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { suggestionHref, SuggestionRow } from './SuggestionItem';
import { useSearchSuggestions } from './useSearchSuggestions';

interface SearchBarProps {
  /** Placeholder text (uses i18n key if not provided). */
  placeholder?: LocalizedText;
  /** Additional className for the container. */
  className?: string;
  /** Visual variant. */
  variant?: ComboboxVariant;
  /** Auto-focus on mount. */
  autoFocus?: boolean;
  /**
   * Controlled query. Provide `value`+`onValueChange` to OWN the query above
   * this component — e.g. `DockableHeroSearch` lifts it so the text survives
   * the dock teleport's remount. Omit both for an internal, uncontrolled query.
   */
  value?: string;
  onValueChange?: (value: string) => void;
  /**
   * Browse mode a district/estate selection navigates into (`/listings/{mode}`).
   * Defaults to `rent` (the home search has no mode context); the listings
   * pages pass their current mode so a selection STAYS on rent/buy.
   */
  mode?: ListingsMode;
  /**
   * Called after a suggestion is chosen and navigation has been issued.
   *
   * For hosts that are TRANSIENT surfaces — the mobile search overlay — which
   * must dismiss themselves once the selection has been made. The navigation
   * itself is the anchor's; the host only learns that it happened.
   */
  onSelect?: () => void;
}

export function SearchBar({
  placeholder,
  className,
  variant = 'default',
  autoFocus = false,
  value,
  onValueChange,
  mode = 'rent',
  onSelect,
}: SearchBarProps) {
  const t = useTranslations('search');
  const tSections = useTranslations('search.sections');
  const locale = useLocale();
  const urlLocale = useUrlLocale();
  const router = useRouter();

  const [internalQuery, setInternalQuery] = useState('');
  const query = value ?? internalQuery;
  const setQuery = onValueChange ?? setInternalQuery;

  const { state, isLoading } = useSearchSuggestions(query);
  const clear = () => setQuery('');

  /** After a suggestion's own anchor has navigated: tidy up and tell the host. */
  const afterSelect = () => {
    clear();
    onSelect?.();
  };

  /**
   * Free-text search — Enter with nothing highlighted, or the search icon.
   *
   * `/listings/{mode}?query=…` is the destination the routes package documents
   * for exactly this ("search UIs navigate to /listings/{mode}?… via the
   * builders"; there is no `/search` route), and the listings loader already
   * reads `query` into `SearchFilters.query`. It is ALSO the native form's
   * `action` + `name` below — the same builder and the same codec constant —
   * so the URL the browser would GET with no JS and the one the SPA pushes
   * cannot drift apart. Before this, the omnibox had no submit path at ALL:
   * Enter was swallowed by the primitive and the icon was decoration, so a
   * query with no suggestions had no way forward (LET-78).
   */
  const submit = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return;
    void router.push(routes.listings.browse(mode, { query: trimmed }));
    afterSelect();
  };

  // The form's GET target carries the URL locale — a bare `/listings/rent`
  // would land a no-JS submit on the default locale (the ADR 0142 class).
  const searchAction = localizeHref(routes.listings.browse(mode), urlLocale);

  // Only the `results` state contributes selectable options — the primitive's
  // keyboard ring and open-derivation both read this, so a notice can never be
  // "highlighted" or counted as a match.
  const sections = state.kind === 'results' ? state.results : [];
  const optionCount = sections.reduce((n, s) => n + s.items.length, 0);

  // Flat (cross-group) index drives keyboard highlight in the primitive.
  let flatIndex = 0;

  function panelBody(): ReactNode {
    switch (state.kind) {
      case 'idle':
        // Below the search SSOT's gate: nothing has been asked, so nothing is
        // claimed. The panel is closed anyway (no options, no notice).
        return null;
      case 'loading':
        return <ComboboxNotice title={t('suggestions.loading')} />;
      case 'error':
        return (
          <ComboboxNotice
            title={t('suggestions.errorTitle')}
            description={t('suggestions.errorHint')}
          />
        );
      case 'empty':
        return (
          <ComboboxNotice
            title={t('suggestions.emptyTitle', { query: state.query })}
            description={t('suggestions.emptyHint')}
          />
        );
      case 'results':
        return state.results.map((section) => {
          if (section.items.length === 0) return null;
          // Map proto SuggestionType variant -> i18n header key (plural).
          // lint-allow: raw-wire-alias -- `storefront.v1.SuggestionType` has no
          // wire_aliases_gen entry (canonical wire form IS the lowercase short
          // name); narrowed via `SuggestionTypeValue` so unknown values fail at
          // compile time.
          const headerKey =
            section.type === 'district'
              ? 'districts'
              : section.type === 'estate'
                ? 'estates'
                : 'buildings';

          return (
            <ComboboxGroup key={section.type} label={tSections(headerKey)}>
              {section.items.map((item) => (
                // The suggestion IS its URL: the primitive merges the option
                // a11y onto this anchor, and activation — click or Enter — is
                // the anchor's own client navigation.
                <ComboboxOption key={item.id} index={flatIndex++} asChild onSelect={afterSelect}>
                  <Link href={suggestionHref(item, locale, mode)} prefetch="intent">
                    <SuggestionRow item={item} mode={mode} />
                  </Link>
                </ComboboxOption>
              ))}
            </ComboboxGroup>
          );
        });
      default:
        return assertNever(state);
    }
  }

  return (
    <Combobox
      value={query}
      onValueChange={setQuery}
      optionCount={optionCount}
      hasNotice={suggestionViewStateHasNotice(state)}
      action={searchAction}
      name={LISTINGS_FILTER_PARAM_NAMES.query}
      onSubmit={submit}
      variant={variant}
      className={className}
    >
      <ComboboxInput
        placeholder={placeholder ?? t('placeholder')}
        aria-label={t('placeholder')}
        icon={<Search />}
        loading={isLoading}
        onClear={clear}
        clearLabel={t('clear')}
        submitLabel={t('submit')}
        autoFocus={autoFocus}
      />
      <ComboboxPanel>{panelBody()}</ComboboxPanel>
    </Combobox>
  );
}
